import { useQuery } from '@tanstack/react-query';

import { agentExecutionsService } from '../services/agentExecutions';

export type OutputEvaluationMetrics = {
  totalExecutions: number;
  avgQualityScore: number;
  passed: number;
  failed: number;
  notEvaluated: number;
};

type UseOutputEvaluationMetricsResult = {
  metrics: OutputEvaluationMetrics;
  isPending: boolean;
  error: Error | null;
};

/**
 * Hook to fetch output evaluation metrics for a specialized agent.
 *
 * Fetches aggregated metrics from the backend including:
 * - Total executions
 * - Average evaluation score (from evaluation_score field)
 * - Passed count (executions with score >= baseline)
 * - Failed count (executions with score < baseline)
 * - Not evaluated count (executions without evaluation_score)
 *
 * @param instanceId - The Opal instance ID
 * @param agentId - The specialized agent ID
 * @param agentVersion - The agent version to filter executions
 * @param baselineScore - The baseline score to determine pass/fail (defaults to 70)
 * @returns Metrics object with evaluation statistics, loading state, and error
 */
export function useOutputEvaluationMetrics({
  instanceId,
  agentId,
  agentVersion,
  baselineScore = 70,
}: {
  instanceId: string;
  agentId: string;
  agentVersion: number;
  baselineScore?: number;
}): UseOutputEvaluationMetricsResult {
  const { data, isPending, error } = useQuery({
    queryKey: ['outputEvaluationMetrics', instanceId, agentId, agentVersion, baselineScore],
    queryFn: () =>
      agentExecutionsService.getOutputEvaluationMetrics(
        instanceId,
        agentId,
        agentVersion,
        baselineScore
      ),
    enabled: !!instanceId && !!agentId && agentVersion !== undefined && agentVersion !== null,
    staleTime: 1000 * 5 * 60, // Cache for 5 minutes
    retry: 1,
  });

  // Transform backend response to match frontend interface
  const metrics: OutputEvaluationMetrics = data
    ? {
        totalExecutions: data.total_executions,
        avgQualityScore: data.avg_quality_score,
        passed: data.passed,
        failed: data.failed,
        notEvaluated: data.not_evaluated,
      }
    : {
        totalExecutions: 0,
        avgQualityScore: 0,
        passed: 0,
        failed: 0,
        notEvaluated: 0,
      };

  return {
    metrics,
    isPending,
    error: error as Error | null,
  };
}
